/**
 * ProseMirror selection에서 현재 선택된 테이블 셀의 위치(pos)를 반환한다.
 * CellSelection이면 선택된 모든 셀, 일반 selection이면 커서가 있는 셀.
 * prosemirror-tables 직접 임포트 없이 duck-typing으로 CellSelection을 감지한다.
 */
export function getSelectedCellPositions(editor: any): number[] {
  const tiptap = editor._tiptapEditor;
  if (!tiptap) return [];

  const { state } = tiptap;
  const { selection } = state;

  if (typeof selection.forEachCell === "function") {
    const positions: number[] = [];
    selection.forEachCell((_node: any, pos: number) => {
      positions.push(pos);
    });
    return positions;
  }

  const $pos = selection.$from;
  for (let depth = $pos.depth; depth > 0; depth--) {
    const node = $pos.node(depth);
    if (
      node.type.name === "tableCell" ||
      node.type.name === "tableHeader"
    ) {
      return [$pos.before(depth)];
    }
  }

  return [];
}

/**
 * 주어진 셀 위치들에 노드 attr(예: backgroundColor/textColor/textAlignment)를
 * setNodeMarkup으로 일괄 설정한다. setNodeMarkup은 문서 구조를 바꾸지 않으므로
 * **CellSelection이 유지**된다(updateBlock은 내용 전체를 교체해 선택이 풀림).
 *
 * backgroundColor/textColor는 BlockNote의 글로벌 attribute(tableCell/tableHeader),
 * textAlignment도 셀 노드 attr이므로 setNodeMarkup에 유효하다.
 *
 * @returns 적용된 셀이 하나라도 있으면 true(= 표 셀 컨텍스트), 아니면 false.
 */
export function setCellAttrAtPositions(
  editor: any,
  positions: number[],
  attr: string,
  value: unknown,
): boolean {
  const tiptap = editor?._tiptapEditor;
  if (!tiptap || positions.length === 0) return false;

  let tr = tiptap.state.tr;
  let changed = false;
  for (const pos of positions) {
    const node = tr.doc.nodeAt(pos);
    if (
      node &&
      (node.type.name === "tableCell" || node.type.name === "tableHeader")
    ) {
      // setNodeMarkup은 attr만 바꾸고 위치/크기는 그대로 → 선택 매핑이 유지됨.
      tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, [attr]: value });
      changed = true;
    }
  }
  if (changed) {
    tiptap.view?.dispatch(tr);
  }
  return changed;
}

/**
 * 현재 selection의 셀(들)에 attr를 설정한다. 표 셀 컨텍스트가 아니면 false 반환
 * → 호출부가 블록/인라인 폴백을 적용할 수 있다.
 */
export function setSelectedCellsAttr(
  editor: any,
  attr: string,
  value: unknown,
): boolean {
  return setCellAttrAtPositions(
    editor,
    getSelectedCellPositions(editor),
    attr,
    value,
  );
}

/** 현재 selection이 표 셀 안인지(단일/다중 무관). */
export function isInTableCell(editor: any): boolean {
  return getSelectedCellPositions(editor).length > 0;
}

/** 첫 선택 셀의 노드 attr 값을 읽는다(활성 상태 표시용). 없으면 undefined. */
export function getFirstSelectedCellAttr(
  editor: any,
  attr: string,
): unknown {
  const tiptap = editor?._tiptapEditor;
  const positions = getSelectedCellPositions(editor);
  if (!tiptap || positions.length === 0) return undefined;
  const node = tiptap.state.doc.nodeAt(positions[0]);
  return node?.attrs?.[attr];
}

/**
 * blockId로 식별되는 table 블록의 ProseMirror 노드 pos를 찾는다.
 * blockContainer(id 일치) > firstChild(table)의 pos. 없으면 -1.
 */
export function findTableNodePos(tiptap: any, blockId: string): number {
  let tablePos = -1;
  tiptap.state.doc.descendants((node: any, pos: number) => {
    if (tablePos !== -1) return false;
    if (
      node.type.name === "blockContainer" &&
      node.attrs?.id === blockId &&
      node.firstChild?.type.name === "table"
    ) {
      // blockContainer content 시작(= table 노드 pos)
      tablePos = pos + 1;
      return false;
    }
    return undefined;
  });
  return tablePos;
}

/**
 * table 블록의 노드 attr `tableAlignment`를 설정한다(setNodeMarkup → 구조/선택 유지).
 * @returns 적용 성공 여부.
 */
export function setTableAlignment(
  editor: any,
  blockId: string,
  alignment: "left" | "center" | "right",
): boolean {
  const tiptap = editor?._tiptapEditor;
  if (!tiptap || !blockId) return false;
  const tablePos = findTableNodePos(tiptap, blockId);
  if (tablePos < 0) return false;
  const node = tiptap.state.doc.nodeAt(tablePos);
  if (!node || node.type.name !== "table") return false;
  tiptap.view?.dispatch(
    tiptap.state.tr.setNodeMarkup(tablePos, undefined, {
      ...node.attrs,
      tableAlignment: alignment,
    }),
  );
  return true;
}

/** table 블록의 현재 정렬값을 읽는다(활성 상태 표시용). 기본 "left". */
export function getTableAlignment(editor: any, blockId: string): string {
  const tiptap = editor?._tiptapEditor;
  if (!tiptap || !blockId) return "left";
  const tablePos = findTableNodePos(tiptap, blockId);
  if (tablePos < 0) return "left";
  const node = tiptap.state.doc.nodeAt(tablePos);
  return (node?.attrs?.tableAlignment as string) || "left";
}
